import type {
    AttributeDefinitionDraft,
    AttributeEnumType,
    AttributeLocalizedEnumType,
    CustomObject,
    CustomObjectDraft,
    LocalizedString,
} from '@commercetools/platform-sdk';
import type { ByProjectKeyRequestBuilder } from '@commercetools/platform-sdk/dist/declarations/src/generated/client/by-project-key-request-builder';
import type { ByProjectKeyTypesRequestBuilder } from '@commercetools/platform-sdk/dist/declarations/src/generated/client/types/by-project-key-types-request-builder';
import type { Client } from '@commercetools/sdk-client-v2';

export type AttributeEnumValue = {
    key: string;
    label: string | LocalizedString;
};
export type AttrEnumType = AttributeEnumType | AttributeLocalizedEnumType;
export const isEnum = (attr?: AttributeDefinitionDraft) => attr?.type.name === 'lenum' || attr?.type.name === 'enum';
export const isLocalizedEnum = (attr?: AttributeDefinitionDraft) => attr?.type.name === 'lenum';
export type AttributeDefinitionHint = {
    previous?: AttributeDefinitionDraft;
    next?: AttributeDefinitionDraft;
};
export type AttributeHint = {
    attributeName?: string;
    isLocalized: boolean;
};
export type AttributeEnumValueHint = {
    previous?: AttributeEnumValue;
    next?: AttributeEnumValue;
    hint?: AttributeHint;
};
export type NestedValuesChanges = {
    nestedValuesChanges: {
        attributeDefinitions: AttributeDefinitionHint[];
        attributeEnumValues: AttributeEnumValueHint[];
    };
};

export type { Store } from '@commercetools/platform-sdk';

export interface ClientConfig {
    get projectKey(): string;
    get clientId(): string;
    get clientSecret(): string;
    get authUrl(): string;
    get apiUrl(): string;
}

export interface ApiClientDetails {
    ctpClient: () => Client;
    apiRoot: () => ByProjectKeyRequestBuilder;
    clientConfig: ClientConfig;
}

export type QueryArgs = Parameters<ByProjectKeyTypesRequestBuilder['get']>[0];

export interface CustomObjectSchemaAttribute {
    name: string;
    type: string;
    set: boolean;
    required: boolean;
}

export interface CustomObjectSchemaEnumAttribute extends CustomObjectSchemaAttribute {
    lenum: [
        {
            value: string;
            label: LocalizedString;
        },
    ];
}

export type PatchExecutionStatus = 'error' | 'success';

export interface PatchExecutionData {
    status: PatchExecutionStatus;
    startDate: Date;
    endDate: Date;
    duration: number;
    logs: unknown[];
}

export interface PatchData {
    status: PatchExecutionStatus;
    name: string;
    extension?: string;
    executions: PatchExecutionData[];
}

export interface PatchCustomObjectDraft extends CustomObjectDraft {
    container: 'ct-patch';
    value: PatchData;
}

export interface PatchCustomObject extends CustomObject {
    value: PatchData;
}

export interface VersionedResource {
    id: string;
    version: number;
}

export type Expand = string | string[];

export const psWebStoreCustomTypeKey = 'petsmart-web-store';

export enum StoreCustomFields {
    MAX_AVAILABILITY_QUANTITY = 'maximumAvailableQuantity',
    MAX_ORDER_QUANTITY = 'maximumOrderQuantity',
    MAX_NUMBER_OF_GIFTCARDS = 'maximumNumberOfGiftCards',
    GIFTCARD_RECAPTCHA_THRESHOLD = 'giftCardRecaptchaThreshold',
    AUTOSHIP_ENABLED = 'autoshipEnabled',
    BOPIS_ENABLED = 'bopisEnabled',
    SAME_DAY_DELIVERY_ENABLED = 'samedayDeliveryEnabled',
    SHIP_TO_HOME_ENABLED = 'shipToHomeEnabled',
    IS_DELIVERY_STORE = 'isDeliveryStore',
    SAME_DAY_DELIVERY_STORE_RADIUS = 'samedayDeliveryStoreRadius',
    SAME_DAY_DELIVERY_DISPLAY_TIME_WINDOWS_COUNT = 'samedayDeliveryDisplayedTimeWindowsCount',
    SAME_DAY_DELIVERY_TIME_WINDOWS = 'samedayDeliveryTimeWindows',
    SAME_DAY_DELIVERY_TIP_COPY = 'samedayDeliveryTipCopy',
    SAME_DAY_DELIVERY_TIP_INSTRUCTION_COPY = 'samedayDeliveryTipInstructionsCopy',
    DELIVERY_TIPS_ITEMS_IN_PERCENT = 'deliveryTipItemsInPercents',
    DEFAULTDELIVERY_TIP_VALUE_IN_PERCENT = 'defaultDeliveryTipValueInPercents',
    VET_SEARCH_RESULT_LIMIT = 'vetSearchResultLimit',
    BUSINESS_STREET_LINE1 = 'businessStreetLine1',
    BUSINESS_STREET_LINE2 = 'businessStreetLine2',
    BUSINESS_CITY = 'businessCity',
    BUSINESS_STATE_CODE = 'businessStateCode',
    BUSINESS_POSTAL_CODE = 'businessPostalCode',
    BUSINESS_COUNTRY = 'businessCountry',
    AFTERPAY_UPPER_LIMIT = 'afterpayUpperLimit',
    AFTERPAY_LOWER_LIMIT = 'afterpayLowerLimit',
    TRACKING_URLS = 'trackingUrls',
    SAME_DAY_DELIVERY_PICKUP_INSTRUCTIONS = 'samedayDeliveryPickupInstructions',
    DELIVERY_MAXIMUM_CART_WEIGHT = 'delivMaximumCartWeight',
    STANDARD_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'standardProductsGuaranteedDeliveryCopy',
    VET_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'vetProductsGuaranteedDeliveryCopy',
    COLD_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'coldProductsGuaranteedDeliveryCopy',
    VET_RX_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'vetRxProductsGuaranteedDeliveryCopy',
    VET_DIET_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'vetDietProductsGuaranteedDeliveryCopy',
    DROPSHIP_PRODUCTS_GUARANTEED_DELIVERY_COPY = 'dropshipProductsGuaranteedDeliveryCopy',
    AUTOSHIP_UPDATED_SUBSCRIPTIONS_PERIOD = 'autoshipUpdatedSubscriptionsPeriod',
    ASSOCIATES_AUTOSHIP_ORDERS_DISCOUNT = 'associatesAutoshipOrdersDiscount',
    SCHEDULED_DELIVERY_MIN_DAYS = 'scheduledDeliveryMinDays',
    SCHEDULED_DELIVERY_MAX_DAYS = 'scheduledDeliveryMaxDays',
    ORDERGROOVE_OFFER_ID = 'orderGrooveOfferId',
    AUTOSHIP_CURRENT_OFFER = 'autoshipCurrentOffer',
}

export enum Locale {
    EN_US = 'en-US',
    EN_CA = 'en-CA',
}

export function localizedString(value: string, locale = Locale.EN_US): LocalizedString {
    return {
        [locale]: value,
    };
}

export const DISCONTINUED_MAX_CENTS = 3;
